import {
  ProductTypeEnum,
  type ProductDeposit,
  type ProductOptionChoice
} from "@kanpla/api-contract";

import { autoId } from "~/utils/auto-id";
import type {
  Product,
  Discount,
  Category,
  StampCardDiscount
} from "~/api/products/types";
import { calculateUnitPriceByUnitSystem } from "~/utils/calculateUnitPriceByUnitSystem";
import { calculateUnitPriceInclVat } from "~/utils/calculate-unit-price-incl-vat";
import { resolveStampCardBaseDiscount } from "~/lib/sale-engine";

import {
  type BasketItemVariant,
  type BasketItemWeighted,
  type BasketItem
} from "../../types";

import {
  type KioskDisplayBasketItem,
  type OrderingProductBasket
} from "./types";
import { mapBasketChoicesToVariants } from "./map-basket-choices-to-variants";
import {
  resolveLineDeposit,
  resolveVariantDeposits
} from "./resolve-line-deposit";
import { calculateVariantsGrossPrice } from "./calculate-variants-gross-price";
import { toPerUnitDeduction } from "./to-per-unit-deduction";

type ParseOrderingProductBasketArgs = {
  product: Product;
  displayProductBasket: KioskDisplayBasketItem;
  productInBasket: BasketItem;
  hybridBillingDiscounts: Discount[];
  stampCardDiscounts: StampCardDiscount[] | undefined;
  categories: Category[];
  // Capped unitPrice-allowance deduction (line total, incl VAT) for this line (0 when none).
  allowanceLineTotal?: number;
};

export function parseOrderingProductBasket({
  product,
  displayProductBasket,
  productInBasket,
  hybridBillingDiscounts,
  stampCardDiscounts,
  categories,
  allowanceLineTotal = 0
}: ParseOrderingProductBasketArgs): OrderingProductBasket {
  let variantUnitGrossPrice = 0;
  let weightedUnitPriceExclVat: number | null = null;

  // Get product choices for price calculations (flat choices from product.variants / product.options)
  const productChoices =
    product.type === ProductTypeEnum.VARIANTS
      ? product.options.flatMap((option) =>
          option.choices.map((choice) => ({
            ...choice,
            optionId: option.id
          }))
        )
      : [];

  if (
    productInBasket.type === "variant" &&
    product.type === ProductTypeEnum.VARIANTS
  ) {
    const variantChoiceMap = mapBasketChoicesToVariants(
      productInBasket.choices,
      productChoices
    );

    variantUnitGrossPrice = calculateVariantsGrossPrice(variantChoiceMap);
  }

  let pricedProduct = product;

  if (
    productInBasket.type === "weighted" &&
    "unitSystem" in product &&
    product.unitSystem !== "piece"
  ) {
    weightedUnitPriceExclVat = calculateUnitPriceByUnitSystem({
      unitPrice: product.unitPriceExclVat,
      weight: productInBasket.weight,
      unitSystem: product.unitSystem,
      weightUnit: productInBasket.weightUnit
    });

    pricedProduct = {
      ...pricedProduct,
      unitPriceExclVat: weightedUnitPriceExclVat,
      unitPriceInclVat:
        weightedUnitPriceExclVat !== null
          ? calculateUnitPriceInclVat(
              weightedUnitPriceExclVat,
              product.taxRate.vatRate,
              null
            )
          : product.unitPriceInclVat
    };
  }

  const stampCardDiscount = stampCardDiscounts
    ? getStampCardDiscount(
        pricedProduct,
        stampCardDiscounts,
        productInBasket,
        displayProductBasket.quantity
      )
    : undefined;

  const hybridBillingDiscount = hybridBillingDiscounts
    ? getHybridBillingDiscount(
        pricedProduct,
        productInBasket,
        hybridBillingDiscounts,
        variantUnitGrossPrice,
        allowanceLineTotal
      )
    : undefined;

  const category = categories.find(
    (category) => category.id === displayProductBasket.product.categoryId
  );

  const deposit = resolveLineDeposit(product);

  return {
    amount: displayProductBasket.quantity,
    name: displayProductBasket.product.name,
    id: displayProductBasket.product.id,
    type:
      displayProductBasket.product.type === "weighted"
        ? "variant-weight"
        : displayProductBasket.product.type,

    category: category?.name ?? "unknown",
    vatRate: pricedProduct.taxRate.vatRate,
    unitPrice: pricedProduct.unitPriceExclVat,
    productLineId: pricedProduct.productLineId,
    taxRateName: pricedProduct.taxRate.name,
    taxRateId: pricedProduct.taxRate.id,

    unitDiscountedPrice: stampCardDiscount?.discountUnitPrice,
    stampCardRuleId: stampCardDiscount?.stampCardRuleId,
    discount: hybridBillingDiscount,

    // Per unit, never per line: the server multiplies by `amount` itself.
    ...(deposit && {
      deposit: {
        depositReturnSchemeId: deposit.depositReturnSchemeId,
        feeExclVat: deposit.feeExclVat,
        feeInclVat: deposit.feeInclVat
      }
    }),

    ...(productInBasket.type === "variant" && {
      options: parseOrderingProductVariant(
        productChoices,
        productInBasket.choices,
        resolveVariantDeposits(product, productInBasket.choices)
      )
    }),
    ...(productInBasket.type === "weighted" && {
      options: parseOrderingProductWeight(productInBasket)
    })
  };
}

function parseOrderingProductVariant(
  productChoices: (ProductOptionChoice & { optionId: string })[],
  choices: BasketItemVariant["choices"],
  variantDeposits: Record<string, ProductDeposit>
): OrderingProductBasket["options"] {
  if (!choices?.length) return undefined;

  const productChoiceMap = new Map(
    productChoices.map((choice) => [choice.id, choice])
  );

  const optionsMap = new Map<
    string,
    NonNullable<OrderingProductBasket["options"]>[number]
  >();

  for (const choice of choices) {
    // Use pre-calculated price from product.variants
    const productChoiceData = productChoiceMap.get(choice.id);

    if (!productChoiceData || productChoiceData.optionId !== choice.variantId)
      continue;

    if (!optionsMap.has(choice.variantId)) {
      optionsMap.set(choice.variantId, {
        id: choice.variantId,
        choices: []
      });
    }

    const deposit = variantDeposits[choice.id];

    optionsMap.get(choice.variantId)?.choices.push({
      id: choice.id,
      name: productChoiceData.name,
      amount: choice.quantity,
      // Use pre-calculated unitPriceExclVat from product.variants
      unitPrice: productChoiceData.unitPriceExclVat,
      // Per unit of the choice; the server multiplies by the amounts itself.
      ...(deposit && {
        deposit: {
          depositReturnSchemeId: deposit.depositReturnSchemeId,
          feeExclVat: deposit.feeExclVat,
          feeInclVat: deposit.feeInclVat
        }
      })
    });
  }

  const result = Array.from(optionsMap.values());

  return result.length > 0 ? result : undefined;
}

/**
 * A gram reading keeps the payload it has always had; the server reads a
 * choice without a unit as grams, so metric orders stay byte-identical.
 */
function parseOrderingProductWeight({
  weight,
  weightUnit,
  weightDecimals,
  unitSystem
}: BasketItemWeighted): OrderingProductBasket["options"] {
  return [
    {
      id: autoId(),
      choices: [
        {
          id: autoId(),
          amount: 1,
          name: unitSystem,
          weight,
          ...(weightUnit !== "g" && { weightUnit, weightDecimals })
        }
      ]
    }
  ];
}

function getStampCardDiscount(
  product: Product,
  stampCardDiscounts: StampCardDiscount[],
  productInBasket: BasketItem,
  quantity: number
): { stampCardRuleId: string; discountUnitPrice: number } | undefined {
  const stampCardDiscountInBasket = productInBasket.discounts.find(
    (discount) => discount.type === "stamp-card"
  );

  const stampCardDiscount = stampCardDiscounts?.find(
    (discount) => discount.stampCardRuleId === stampCardDiscountInBasket?.id
  );

  if (!stampCardDiscount?.stampCardRuleId) return undefined;

  const lineDiscount = resolveStampCardBaseDiscount({
    baseUnitPriceInclVat: product.unitPriceInclVat,
    discountType: stampCardDiscount.discountType,
    discount: stampCardDiscount.discount
  });

  return {
    stampCardRuleId: stampCardDiscount.stampCardRuleId,
    discountUnitPrice: toPerUnitDeduction(lineDiscount, quantity)
  };
}

const getHybridBillingDiscount = (
  product: Product,
  productInBasket: BasketItem,
  hybridBillingDiscounts: Discount[],
  variantUnitGrossPrice: number,
  allowanceLineTotal: number
): OrderingProductBasket["discount"] | undefined => {
  const hybridBillingDiscountInBasket = productInBasket.discounts.find(
    (discount) => discount.type === "hybrid-billing"
  );

  const hybridBillingDiscount = hybridBillingDiscounts.find(
    (hybridBillingDiscount) =>
      hybridBillingDiscount.id === hybridBillingDiscountInBasket?.id
  );

  if (!hybridBillingDiscount) return undefined;

  const allowanceType = product.discount?.allowanceType ?? null;

  // unitPrice allowance: amount is the capped line total (like POS), 0 when over
  // budget. v4 transport divides it back to per-unit.
  if (allowanceType === "unitPrice") {
    return {
      type: "discount-hybridBilling",
      percentage: 0,
      amount: allowanceLineTotal,
      id: hybridBillingDiscount.id,
      name: hybridBillingDiscount.name,
      allowanceType
    };
  }

  const unitTotalPriceInclVat =
    product.unitPriceInclVat + variantUnitGrossPrice;

  let amount = 0;
  let percentage = 0;

  if (hybridBillingDiscount.amountType === "percentage") {
    percentage = hybridBillingDiscount.amount;
    amount = unitTotalPriceInclVat * (percentage / 100);
  } else {
    amount = hybridBillingDiscount.amount;
    percentage = (amount / unitTotalPriceInclVat) * 100;
  }

  return {
    type: "discount-hybridBilling",
    percentage,
    amount,
    id: hybridBillingDiscount.id,
    name: hybridBillingDiscount.name,
    allowanceType
  };
};
